import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import type { TemplateInstallMode, TemplateListItem } from '@/api/types'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Spinner } from '@/shared/ui/spinner'
import { installedTarget } from '../gallery'
import { useInstallTemplate } from '../queries'

const MODES: readonly TemplateInstallMode[] = ['everything', 'databases']

/**
 * Asks what to create from a template, then goes to it.
 *
 * The choice is only offered when the template has a dashboard: one without
 * creates the same thing either way.
 */
export function InstallTemplateDialog({
  workspaceId,
  template,
  open,
  onOpenChange,
}: {
  workspaceId: number
  template: TemplateListItem
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const install = useInstallTemplate()
  const [mode, setMode] = useState<TemplateInstallMode>('everything')
  const choice = template.counts.dashboards > 0

  function submit() {
    install.mutate(
      { workspaceId, templateId: template.id, mode: choice ? mode : 'databases' },
      {
        onSuccess: (applications) => {
          onOpenChange(false)
          void navigate(installedTarget(applications))
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('templates.install.title', { title: template.title })}
          </DialogTitle>
          <DialogDescription>
            {choice
              ? t('templates.install.description')
              : t('templates.install.databasesOnlyDescription')}
          </DialogDescription>
        </DialogHeader>

        {choice && (
          <fieldset className="space-y-2">
            <legend className="mb-2 text-sm font-medium">
              {t('templates.install.what')}
            </legend>
            {MODES.map((option) => (
              <label
                key={option}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-accent',
                  mode === option && 'border-primary',
                )}
              >
                <input
                  type="radio"
                  name="install-mode"
                  value={option}
                  checked={mode === option}
                  onChange={() => setMode(option)}
                  className="mt-1 accent-primary"
                />
                <span className="space-y-0.5">
                  <span className="block text-sm font-medium">
                    {t(`templates.install.${option}`)}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {t(`templates.install.${option}Hint`)}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={install.isPending}
          >
            {t('action.cancel')}
          </Button>
          <Button type="button" onClick={submit} disabled={install.isPending}>
            {install.isPending && <Spinner />}
            {t('templates.install.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
